import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  FiSearch, 
  FiFilter,
  FiEye,
  FiEdit,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiX,
  FiPackage,
  FiUser,
  FiMapPin,
  FiCreditCard,
  FiCalendar
} from 'react-icons/fi'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { ordersAPI, razorpayAPI } from '../../utils/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const AdminOrders = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('')
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)

  const queryClient = useQueryClient()

  // Fetch orders
  const { data: ordersData, isLoading } = useQuery(
    ['admin-orders', searchTerm, statusFilter, paymentFilter],
    () => ordersAPI.getOrders({ 
      search: searchTerm,
      status: statusFilter,
      paymentStatus: paymentFilter,
      limit: 100 
    })
  )

  const orders = ordersData?.data?.orders || []

  // Update order status mutation
  const updateStatusMutation = useMutation(
    ({ id, status }) => ordersAPI.updateOrderStatus(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        setShowStatusModal(false)
        toast.success('Order status updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update order status')
      }
    }
  )

  // useQueryClient already defined above as queryClient
  const approveCancellation = useMutation(
    (id) => ordersAPI.approveCancellation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        toast.success('Cancellation approved and order cancelled')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve cancellation')
      }
    }
  )
  const denyCancellation = useMutation(
    (id) => ordersAPI.denyCancellation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        toast.success('Cancellation request denied')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to deny cancellation')
      }
    }
  )
  const clearRefundRequest = useMutation(
    (id) => ordersAPI.clearRefundRequest(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        toast.success('Refund request cleared')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to clear refund request')
      }
    }
  )

  const processRefundMutation = useMutation(
    ({ orderId, amount, reason }) => razorpayAPI.processRefund({ orderId, amount, reason }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        setShowRefundModal(false)
        toast.success('Refund processed successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to process refund')
      }
    }
  )

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'yellow' },
    { value: 'confirmed', label: 'Confirmed', color: 'blue' },
    { value: 'processing', label: 'Processing', color: 'purple' },
    { value: 'shipped', label: 'Shipped', color: 'indigo' },
    { value: 'delivered', label: 'Delivered', color: 'green' },
    { value: 'cancelled', label: 'Cancelled', color: 'red' }
  ]

  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending', color: 'yellow' },
    { value: 'paid', label: 'Paid', color: 'green' },
    { value: 'failed', label: 'Failed', color: 'red' },
    { value: 'refunded', label: 'Refunded', color: 'gray' }
  ]

  const getStatusColor = (status) => {
    const option = statusOptions.find(opt => opt.value === status)
    return option ? option.color : 'gray'
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FiClock className="w-4 h-4" />
      case 'confirmed': return <FiCheckCircle className="w-4 h-4" />
      case 'processing': return <FiPackage className="w-4 h-4" />
      case 'shipped': return <FiTruck className="w-4 h-4" />
      case 'delivered': return <FiCheckCircle className="w-4 h-4" />
      case 'cancelled': return <FiAlertCircle className="w-4 h-4" />
      default: return <FiClock className="w-4 h-4" />
    }
  }

  const getPaymentStatusColor = (status) => {
    const option = paymentStatusOptions.find(opt => opt.value === status)
    return option ? option.color : 'gray'
  }

  const handleViewOrder = (order) => {
    setSelectedOrder(order)
    setShowOrderModal(true)
  }

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order)
    setShowStatusModal(true)
  }

  const OrderModal = ({ isOpen, onClose, order }) => {
    if (!order) return null

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Order Details</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Order Header */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Order Information</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Order #:</span> {order.orderNumber}</p>
                    <p><span className="font-medium">Date:</span> {new Date(order.createdAt).toLocaleDateString()}</p>
                    <p><span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full bg-${getStatusColor(order.status)}-100 text-${getStatusColor(order.status)}-800`}>
                        {order.status}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Customer</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Name:</span> {order.user?.name || 'Guest'}</p>
                    <p><span className="font-medium">Email:</span> {order.user?.email || 'N/A'}</p>
                    <p><span className="font-medium">Phone:</span> {order.shippingAddress?.phone || 'N/A'}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Payment</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-medium">Status:</span> 
                      <span className={`ml-2 px-2 py-1 text-xs rounded-full bg-${getPaymentStatusColor(order.paymentStatus)}-100 text-${getPaymentStatusColor(order.paymentStatus)}-800`}>
                        {order.paymentStatus}
                      </span>
                    </p>
                    <p><span className="font-medium">Method:</span> {order.paymentMethod?.type || 'N/A'}</p>
                    <p><span className="font-medium">Total:</span> ${order.total.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Order Items</h3>
                <div className="space-y-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                      <img
                        src={item.image || '/api/placeholder/60/60'}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.name}</h4>
                        <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">${item.price.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">Total: ${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FiMapPin className="mr-2" />
                  Shipping Address
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{order.shippingAddress?.name}</p>
                  <p>{order.shippingAddress?.street}</p>
                  <p>{order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.zipCode}</p>
                  <p>{order.shippingAddress?.country}</p>
                </div>
              </div>

              {/* Order Summary */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>${order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    <span>${order.shipping.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>${order.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span>${order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  onClose()
                  handleUpdateStatus(order)
                }}
                className="btn-primary"
              >
                Update Status
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  const StatusModal = ({ isOpen, onClose, order }) => {
    const [newStatus, setNewStatus] = useState(order?.status || '')

    useEffect(() => {
      if (order) {
        setNewStatus(order.status)
      }
    }, [order])

    const handleSubmit = (e) => {
      e.preventDefault()
      updateStatusMutation.mutate({ id: order._id, status: newStatus })
    }

    if (!order) return null

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Update Order Status</h3>
              <p className="text-gray-600">Order #{order.orderNumber}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateStatusMutation.isLoading}
                  className="btn-primary"
                >
                  {updateStatusMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Update Status'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    )
  }

  const RefundModal = ({ isOpen, onClose, order }) => {
    const [amount, setAmount] = useState(order ? order.total : 0)
    const [reason, setReason] = useState(order?.refundRequestedReason || '')

    useEffect(() => {
      if (order) {
        setAmount(order.refundRequestedAmount || order.total)
        setReason(order.refundRequestedReason || '')
      }
    }, [order])

    const handleSubmit = (e) => {
      e.preventDefault()
      if (!order) return
      if (!amount || Number(amount) <= 0) {
        toast.error('Enter a valid amount')
        return
      }
      processRefundMutation.mutate({ orderId: order._id, amount: Number(amount), reason })
    }

    if (!order) return null

    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Process Refund</h3>
              <p className="text-gray-600">Order #{order.orderNumber}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (INR)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Max: ₹{order.total.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processRefundMutation.isLoading}
                  className="btn-primary"
                >
                  {processRefundMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Process Refund'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Admin Orders - Kommercen</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container-custom">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Orders Management</h1>
            <p className="text-gray-600 mt-2">Manage customer orders and track fulfillment</p>
          </div>

          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Payment Status</option>
                  {paymentStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {orders.length} orders found
                </span>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-600">Orders will appear here when customers make purchases</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            #{order.orderNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {order.user?.name || 'Guest'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.user?.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.items.length} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${order.total.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full bg-${getStatusColor(order.status)}-100 text-${getStatusColor(order.status)}-800 flex items-center w-fit`}>
                            {getStatusIcon(order.status)}
                            <span className="ml-1 capitalize">{order.status}</span>
                          </span>
                          <div className="mt-1 flex gap-2">
                            {order.cancellationRequested && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">Cancel requested</span>
                            )}
                            {order.refundRequested && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">Refund requested</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full bg-${getPaymentStatusColor(order.paymentStatus)}-100 text-${getPaymentStatusColor(order.paymentStatus)}-800`}>
                            {order.paymentStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewOrder(order)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateStatus(order)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FiEdit className="w-4 h-4" />
                            </button>
                            {order.cancellationRequested && (
                              <>
                                <button
                                  onClick={() => approveCancellation.mutate(order._id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Approve cancellation"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => denyCancellation.mutate(order._id)}
                                  className="text-gray-600 hover:text-gray-800"
                                  title="Deny cancellation"
                                >
                                  Deny
                                </button>
                              </>
                            )}
                            {order.paymentStatus === 'paid' && (
                              <button
                                onClick={() => { setSelectedOrder(order); setShowRefundModal(true) }}
                                className="text-indigo-600 hover:text-indigo-800"
                                title="Process refund"
                              >
                                Process Refund
                              </button>
                            )}
                            {order.refundRequested && (
                              <button
                                onClick={() => clearRefundRequest.mutate(order._id)}
                                className="text-gray-600 hover:text-gray-800"
                                title="Mark refund request handled"
                              >
                                Clear Refund Request
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modals */}
          <OrderModal
            isOpen={showOrderModal}
            onClose={() => {
              setShowOrderModal(false)
              setSelectedOrder(null)
            }}
            order={selectedOrder}
          />

          <StatusModal
            isOpen={showStatusModal}
            onClose={() => {
              setShowStatusModal(false)
              setSelectedOrder(null)
            }}
            order={selectedOrder}
          />

          <RefundModal
            isOpen={showRefundModal}
            onClose={() => {
              setShowRefundModal(false)
              setSelectedOrder(null)
            }}
            order={selectedOrder}
          />
        </div>
      </div>
    </>
  )
}

export default AdminOrders